import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { appointmentService } from "../services/appointmentService";
import { perfStart } from "../utils/perfLog";
import { clientDataService } from "../services/clientDataService";
import { isNewClient } from "../shared/newClientAppointment";
import { barberDataService, type DayAvailability } from "../services/barberDataService";
import { hierarchicalServiceDataService } from "../services/hierarchicalServiceDataService";
import flatServiceDataService from "../services/flatServiceDataService";
import { format } from "date-fns";
import { getDaySchedule, computeDayAvailability, isSlotBookable, parseTimeToMinutes, applyShift, toYMD } from "../shared/availability";
import { shiftService } from "../services/shiftService";
import { resolveServiceDuration } from "../shared/serviceDuration";
import EmergencyBookingTile from "./Booking/EmergencyBookingTile";
import { celebrateConfetti } from "./ui/CelebrationProvider";
import { ymd } from '../shared/dateUtils';
import { normalizePhone } from '../shared/identity';
import NoShowAgreementModal, { type PrepaidAgreement } from './NoShowAgreementModal';
import { usePermission } from '../hooks/usePermission';

interface Barber {
  id: string;
  name: string;
  avatar: string;
  rating: number;
  available: boolean;
  specialties: string[];
  nextSlot: string;
  performance: number;
  // DUAL-SCHEMA day slot (enabled ?? available ?? true) — see shared/availability.ts
  availability?: {
    monday: DayAvailability;
    tuesday: DayAvailability;
    wednesday: DayAvailability;
    thursday: DayAvailability;
    friday: DayAvailability;
    saturday: DayAvailability;
    sunday: DayAvailability;
  };
}

interface Service {
  id: string;
  name: string;
  duration: number;
  price: number;
  popular?: boolean;
  emergencyEligible?: boolean;
}

interface CompleteBookingSystemProps {
  emergencyClient?: any; // Pre-filled client for emergency booking
}

// Fires a (random) confetti burst on the rising edge of `active` — pairs with the existing
// "Booking Confirmed!" modal so it feels alive instead of static. BEHAVIOR-PIN: SLIK celebration.
const BookingConfettiTrigger: React.FC<{ active: boolean }> = ({ active }) => {
  const prev = React.useRef(false);
  React.useEffect(() => {
    if (active && !prev.current) celebrateConfetti();
    prev.current = active;
  }, [active]);
  return null;
};

export const CompleteBookingSystem: React.FC<CompleteBookingSystemProps> = ({ emergencyClient }) => {
  const navigate = useNavigate();

  // Load real appointments to show booked slots
  const [existingAppointments, setExistingAppointments] = useState<any[]>([]);
  const [, setRefreshKey] = useState(0); // Force re-render when appointments change

  useEffect(() => {
    const loadAppointments = () => {
      const appointments = appointmentService.getAllAppointments();
      setExistingAppointments(appointments);
      setRefreshKey(prev => prev + 1); // Force re-render to update slot counts
    };

    loadAppointments();

    // Subscribe to changes
    const unsubscribe = appointmentService.subscribe(() => {
      loadAppointments();
    });

    return () => unsubscribe();
  }, []);

  const [selectedBarber, setSelectedBarber] = useState<Barber | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setViewMode] = useState<"basic" | "advanced">("advanced");
  const [serviceCategory, setServiceCategory] = useState<
    "all" | "hair" | "beard" | "special"
  >("all");
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const { isManager } = usePermission(); // owner/manager → ⚡ squeeze a client into a full/taken slot
  const [squeezeMode, setSqueezeMode] = useState(false);
  // Removed old showEmergencyModal - EmergencyBookingTile has its own modal
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setCheckoutStep] = useState<
    "summary" | "payment" | "confirm"
  >("summary");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setPaymentMethod] = useState<"card" | "cash" | "app">(
    "card",
  );
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [confirmedBookingDetails, setConfirmedBookingDetails] = useState<{
    barber: Barber | null;
    date: Date | null;
    time: string;
    services: Service[];
  } | null>(null);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [filterType, setFilterType] = useState<"all" | "vip" | "new" | "due">(
    "all",
  );
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [selectedCustomerDetails, setSelectedCustomerDetails] =
    useState<any>(null);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthday: '',
    notes: ''
  });

  // Emergency client state (passed to EmergencyBookingTile as clientData prop)
  const [emergencySelectedClient, setEmergencySelectedClient] = useState<any>(null);
  const [, setShowEmergencyModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setEmergencySearchQuery] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setEmergencyService] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setEmergencySelectedBarber] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setEmergencyWalkInName] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setEmergencyWalkInPhone] = useState('');

  // Auto-select emergency client if provided (EmergencyBookingTile will use clientData prop)
  useEffect(() => {
    if (emergencyClient) {
      setEmergencySelectedClient(emergencyClient);
      setShowEmergencyModal(true);
      toast.success(`🚨 Emergency booking opened for ${emergencyClient.name}`, { duration: 4000 });
    }
  }, [emergencyClient]);

  // Check for pre-selected client from client card "Schedule" button
  useEffect(() => {
    const preSelectedClientData = sessionStorage.getItem('preSelectedClient');
    if (preSelectedClientData) {
      try {
        const clientData = JSON.parse(preSelectedClientData);
        setSelectedCustomer(clientData);
        setShowCustomerSearch(false); // Close search modal if open
        // Clear the sessionStorage after using it
        sessionStorage.removeItem('preSelectedClient');
        console.log('📅 Pre-selected client loaded:', clientData.name);
      } catch (error) {
        console.error('Error parsing pre-selected client:', error);
        sessionStorage.removeItem('preSelectedClient');
      }
    }
  }, []);

  // Get real customers from clientDataService or use sample data
  const sampleCustomers = [
    {
      id: "1",
      name: "John Smith",
      phone: "(555) 123-4567",
      email: "john.smith@email.com",
      lastVisit: "2 weeks ago",
      visits: 12,
      favorite: "Haircut",
      totalSpent: 480,
      notes:
        "Prefers short sides, longer on top. Allergic to certain hair products.",
      nextAppointment: null,
      memberSince: "2023",
      tags: ["regular", "tips well"],
    },
    {
      id: "2",
      name: "Emily Johnson",
      phone: "(555) 234-5678",
      email: "emily.j@email.com",
      lastVisit: "1 week ago",
      visits: 24,
      favorite: "Color & Cut",
      totalSpent: 1250,
      notes: "Likes bold colors. Schedule on weekends only.",
      nextAppointment: "Sep 22, 2:00 PM",
      memberSince: "2022",
      tags: ["vip", "color specialist"],
    },
    {
      id: "3",
      name: "Michael Brown",
      phone: "(555) 345-6789",
      email: "mbrown@email.com",
      lastVisit: "3 days ago",
      visits: 8,
      favorite: "Beard Trim",
      totalSpent: 320,
      notes: "New customer, very punctual.",
      nextAppointment: null,
      memberSince: "2024",
      tags: ["new", "punctual"],
    },
    {
      id: "4",
      name: "Sarah Davis",
      phone: "(555) 456-7890",
      email: "sarah.davis@email.com",
      lastVisit: "1 month ago",
      visits: 15,
      favorite: "Highlights",
      totalSpent: 875,
      notes: "Due for root touch-up. Prefers natural tones.",
      nextAppointment: null,
      memberSince: "2023",
      tags: ["due for visit", "highlights"],
    },
    {
      id: "5",
      name: "James Wilson",
      phone: "(555) 567-8901",
      email: "jwilson@email.com",
      lastVisit: "Yesterday",
      visits: 31,
      favorite: "Haircut",
      totalSpent: 1240,
      notes: "VIP customer. Always books with Marcus.",
      nextAppointment: "Sep 28, 10:00 AM",
      memberSince: "2021",
      tags: ["vip", "loyal"],
    },
    {
      id: "6",
      name: "Lisa Anderson",
      phone: "(555) 678-9012",
      email: "lisa.a@email.com",
      lastVisit: "2 weeks ago",
      visits: 19,
      favorite: "Styling",
      totalSpent: 760,
      notes: "Preparing for wedding. Needs special attention.",
      nextAppointment: "Sep 20, 3:00 PM",
      memberSince: "2022",
      tags: ["event", "styling"],
    },
    {
      id: "7",
      name: "Robert Taylor",
      phone: "(555) 789-0123",
      email: "rtaylor@email.com",
      lastVisit: "5 days ago",
      visits: 22,
      favorite: "Haircut & Shave",
      totalSpent: 990,
      notes: "Traditional style. Hot towel shave enthusiast.",
      nextAppointment: null,
      memberSince: "2022",
      tags: ["vip", "traditional"],
    },
    {
      id: "8",
      name: "Jessica Martinez",
      phone: "(555) 890-1234",
      email: "jmartinez@email.com",
      lastVisit: "1 week ago",
      visits: 11,
      favorite: "Color",
      totalSpent: 550,
      notes: "Sensitive scalp. Use gentle products only.",
      nextAppointment: null,
      memberSince: "2023",
      tags: ["sensitive", "color"],
    },
  ];

  // State for real customers
  const [customers, setCustomers] = useState<any[]>(sampleCustomers);

  // Load real customers from clientDataService
  useEffect(() => {
    const loadCustomers = () => {
      const realClients = clientDataService.getAllClients();
      console.log('Loading real clients from service:', realClients);

      if (realClients && realClients.length > 0) {
        // Map clients to customer format
        const mappedCustomers = realClients.map(client => ({
          id: client.id,
          name: `${client.firstName} ${client.lastName}`,
          phone: client.phone,
          email: client.email || '',
          lastVisit: client.lastVisit ? new Date(client.lastVisit).toLocaleDateString() : 'Never',
          visits: client.totalVisits || 0,
          favorite: 'Haircut',
          totalSpent: client.totalSpent || 0,
          notes: client.notes || '',
          nextAppointment: null,
          memberSince: client.joinDate ? new Date(client.joinDate).getFullYear().toString() : '2024',
          tags: client.tags || []
        }));

        setCustomers(mappedCustomers);
      } else {
        // Use sample customers as fallback
        setCustomers(sampleCustomers);
      }
    };

    loadCustomers();

    // Subscribe to client updates
    clientDataService.subscribe((clients) => {
      if (clients && clients.length > 0) {
        const mappedCustomers = clients.map(client => ({
          id: client.id,
          name: `${client.firstName} ${client.lastName}`,
          phone: client.phone,
          email: client.email || '',
          lastVisit: client.lastVisit ? new Date(client.lastVisit).toLocaleDateString() : 'Never',
          visits: client.totalVisits || 0,
          favorite: 'Haircut',
          totalSpent: client.totalSpent || 0,
          notes: client.notes || '',
          nextAppointment: null,
          memberSince: client.joinDate ? new Date(client.joinDate).getFullYear().toString() : '2024',
          tags: client.tags || []
        }));

        setCustomers(mappedCustomers);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFilteredCustomers = () => {
    // PHONE SEARCH (Jose 2026-10-27): normalize both sides to the last-10 key so any format matches.
    const q = searchQuery.trim();
    const qDigits = q.replace(/\D/g, '');
    const qKey = qDigits.length > 10 ? qDigits.slice(-10) : qDigits;
    let filtered = customers.filter(
      (customer) =>
        customer.name.toLowerCase().includes(q.toLowerCase()) ||
        (qKey.length >= 3 && normalizePhone(customer.phone).includes(qKey)) ||
        (customer.email || '').toLowerCase().includes(q.toLowerCase()),
    );

    switch (filterType) {
      case "vip":
        filtered = filtered.filter((c) => c.visits >= 20);
        break;
      case "new":
        filtered = filtered.filter((c) => c.visits < 5);
        break;
      case "due":
        filtered = filtered.filter((c) => {
          const lastVisitText = c.lastVisit.toLowerCase();
          return (
            lastVisitText.includes("month") || lastVisitText.includes("weeks")
          );
        });
        break;
    }

    return filtered;
  };

  const filteredCustomers = getFilteredCustomers();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCustomerClick = (customer: any) => {
    // Set this customer as the selected customer for booking
    setSelectedCustomer(customer);
    setShowCustomerSearch(false);
    toast.success(`Selected ${customer.name} for booking`);

    // If they want details, they can click View Details button instead
    // setSelectedCustomerDetails(customer);
    // setShowCustomerDetails(true);
  };

  const handleQuickBook = async (customer: any) => {
    // Check if we have all required data
    if (!selectedBarber || !selectedDate || !selectedTime) {
      toast.error('Please select a barber, date, and time');
      return;
    }

    if (!selectedServices || selectedServices.length === 0) {
      toast.error('Please select at least one service');
      return;
    }

    // Format the date properly (selectedDate is a Date object)
    // IMPORTANT: Use local timezone, NOT UTC (toISOString converts to UTC and can shift the date!)
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`; // YYYY-MM-DD in LOCAL timezone

    // Convert AM/PM time to 24-hour format
    const convertTo24Hour = (time12h: string) => {
      const [time, modifier] = time12h.split(' ');
      let [hours, minutes] = time.split(':');
      if (!minutes) {
        minutes = '00';
      }
      if (hours === '12') {
        hours = modifier === 'AM' ? '00' : '12';
      } else if (modifier === 'PM') {
        hours = String(parseInt(hours, 10) + 12);
      }
      return `${hours.padStart(2, '0')}:${minutes}`;
    };

    const time24Hour = convertTo24Hour(selectedTime);

    // NEW-CLIENT (2026-08-07): a FIRST-TIMER (never been here) → 45-min min + flagged so the
    // calendar labels it "New Client — [name]". "Been here before" = has prior visits; a brand-new
    // person is usually added as a client THEN booked, so we key off visit count (0 = first-timer),
    // falling back to "not in the client list" for a typed-in walk-in. shared/newClientAppointment.ts
    const qcNewClient = customer
      ? Number((customer as any).visits ?? (customer as any).totalVisits ?? 0) === 0
      : isNewClient(
          { name: customer?.name, phone: customer?.phone, email: (customer as any)?.email },
          clientDataService.getAllClients(),
        );
    const qcBaseDuration = selectedServices.some(s => s.name === 'Haircut') ? 30 : 45;

    // Create appointment data
    const appointmentData = {
      clientId: customer?.id || 'walk-in-' + Date.now(),
      clientName: customer?.name || 'Walk-in Customer',
      clientPhone: customer?.phone || '',
      barberId: selectedBarber.id,  // selectedBarber is an object, use its id
      barberName: selectedBarber.name,
      service: selectedServices.map(s => s.name).join(', '), // selectedServices is array of objects
      date: formattedDate,
      time: time24Hour,
      duration: qcBaseDuration, // new-client (+ 45-min bump) is decided authoritatively in appointmentService.addAppointment
      price: selectedServices.reduce((total, service) => {
        return total + service.price;
      }, 0),
      status: 'scheduled' as const,
      source: 'walk-in' as const,
      reminderSent: false
    };

    console.log('Creating appointment from Quick Checkout:', appointmentData);

    const loadingToast = toast.loading('Booking appointment...');

    try {
      const newAppointment = await appointmentService.addAppointment(appointmentData, { background: true }); // local-first → no 30s spinner (Jose 2026-10-23)

      toast.dismiss(loadingToast);
      toast.success(`Appointment booked for ${customer?.name || 'Walk-in'} on ${selectedDate} at ${selectedTime}`);

      // Reset the form
      setSelectedCustomer(customer);
      setShowCustomerSearch(false);
      setShowCustomerDetails(false);

      // Dispatch custom event to notify Calendar
      console.log('🔔 Dispatching appointmentCreated event for:', newAppointment.id);
      window.dispatchEvent(new CustomEvent('appointmentCreated', {
        detail: newAppointment
      }));

      console.log('✅ Appointment created successfully:', newAppointment);
      console.log('📍 Appointment saved to Firebase and localStorage. Navigating to Calendar...');
      console.log('📅 Appointment date:', formattedDate);

      // Navigate to Operations page (Calendar tab) to show the newly created appointment
      setTimeout(() => {
        navigate('/operations', {
          state: {
            openCalendar: true,
            appointmentDate: formattedDate // Pass the appointment date so calendar can jump to it
          }
        });
      }, 1500); // Small delay to let user see the success toast
    } catch (error: any) {
      toast.dismiss(loadingToast);
      // NO-SHOW BLOCK: open the prepay + e-signature gate instead of dead-ending on a toast.
      if (error?.code === 'NO_SHOW_BLOCK') {
        setBlockGate({
          notice: error.message,
          apptData: appointmentData,
          serviceLabel: appointmentData.service,
          priceLabel: `$${appointmentData.price}`,
          clientName: appointmentData.clientName,
        });
        return;
      }
      console.error('Failed to create appointment:', error);
      // Surface the double-book conflict message (or any validation message) instead of a generic error.
      const msg = error instanceof Error && error.message.startsWith('Conflict:')
        ? error.message.replace(/^Conflict:\s*/, '')
        : 'Failed to book appointment. Please try again.';
      toast.error(msg);
    }
  };

  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [allServices, setAllServices] = useState<Record<string, Service[]>>({
    hair: [],
    beard: [],
    special: []
  });
  const [, setLoadingServices] = useState(true);

  // NO-SHOW BLOCK gate (2026-08-07): when addAppointment throws NO_SHOW_BLOCK, open the prepay +
  // e-signature agreement instead of a dead-end toast, then re-book with the agreement attached.
  const [blockGate, setBlockGate] = useState<{ notice: string; apptData: any; serviceLabel: string; priceLabel: string; clientName: string } | null>(null);
  const [agreeSubmitting, setAgreeSubmitting] = useState(false);

  const submitBlockedPrepaid = async (agreement: PrepaidAgreement) => {
    if (!blockGate) return;
    setAgreeSubmitting(true);
    try {
      const created = await appointmentService.addAppointment({ ...blockGate.apptData, prepaidAgreement: agreement });
      setBlockGate(null);
      toast.success('Booked — payment collected upfront per the signed agreement.');
      window.dispatchEvent(new CustomEvent('appointmentCreated', { detail: created }));
    } catch (e: any) {
      toast.error(e?.message || 'Could not complete the booking.');
    } finally {
      setAgreeSubmitting(false);
    }
  };

  // Load barbers from Firebase
  useEffect(() => {
    const loadBarbers = async () => {
      const barbersData = await barberDataService.getAllBarbers();
      const avatars = ["😎", "😄", "😊", "🔥", "💈", "✨", "⭐"];
      setBarbers(barbersData.map((b, idx) => ({
        id: b.id,
        name: `${b.firstName} ${b.lastName}`,
        avatar: b.photo || avatars[idx % avatars.length],
        rating: b.avgRating || 4.8,
        available: b.status === 'active',
        specialties: b.skills || [],
        nextSlot: "Available",
        performance: Math.round((b.returningClients / b.totalClients) * 100) || 0, // was `|| 85` (invented a fake 85% when the raw seed fields were 0/0). Audit fix.
        availability: b.availability,
        // role drives the Emergency tile's owner/manager after-hours override (2026-08-06).
        role: (b as any).role,
      })));
    };
    loadBarbers();
  }, []);

  // Load services from Firebase
  useEffect(() => {
    const loadServices = async () => {
      try {
        setLoadingServices(true);
        // ONE BRAIN (Jose 2026-10-21): read the SAME source the Book Now picker uses — flatServiceDataService
        // — not hierarchicalServiceDataService, whose getActiveCategories() DROPS flat categories (Beard /
        // Specialty have no subcategories) and only surfaces subcategory variations → the Appointments tab
        // came up with NO services to pick. The flat brain returns every category's full `services[]`.
        const categories = await flatServiceDataService.getActiveCategories();

        const hair: Service[] = [];
        const beard: Service[] = [];
        const special: Service[] = [];

        console.log('📋 Flat one-brain categories:', categories.map(c => `${c.name}(${c.services?.length || 0})`));
        categories.forEach(category => {
          const categoryLower = category.name.toLowerCase();
          (category.services || []).forEach(variation => {
            const service: Service = {
              id: variation.id,
              name: variation.name,
              duration: variation.duration,
              price: variation.price,
              popular: false,
              emergencyEligible: variation.emergencyEligible || false
            };
            const varLower = variation.name.toLowerCase();
            const isHair = categoryLower.includes('haircut') || categoryLower.includes('hair') || categoryLower.includes('cut')
              || varLower.includes('haircut') || varLower.includes('fade') || varLower.includes('taper');
            const isBeard = categoryLower.includes('beard') || categoryLower.includes('shave') || categoryLower.includes('facial')
              || varLower.includes('beard') || varLower.includes('shave') || varLower.includes('lineup');
            if (isHair) hair.push(service);
            else if (isBeard) beard.push(service);
            else special.push(service);
          });
        });

        setAllServices({ hair, beard, special });
        console.log('Services loaded (flat one-brain):', { hair: hair.length, beard: beard.length, special: special.length });
      } catch (error) {
        console.error('Error loading services from Firebase:', error);
        setAllServices({ hair: [], beard: [], special: [] });
      } finally {
        setLoadingServices(false);
      }
    };
    loadServices();
  }, []);

  // EmergencyBookingTile now handles barber selection internally

  // Barbers and services are now loaded from Firebase via useEffect above

  const services =
    serviceCategory === "all"
      ? (() => {
          // Combine all services and deduplicate by ID to avoid React key errors
          const allServicesList = [
            ...allServices.hair,
            ...allServices.beard,
            ...allServices.special
          ];
          const uniqueServices = new Map<string, Service>();
          allServicesList.forEach(service => {
            if (!uniqueServices.has(service.id)) {
              uniqueServices.set(service.id, service);
            }
          });
          return Array.from(uniqueServices.values());
        })()
      : allServices[serviceCategory] || [];

  const timeSlots = [
    "9:00 AM",
    "9:30 AM",
    "10:00 AM",
    "10:30 AM",
    "11:00 AM",
    "11:30 AM",
    "12:00 PM",
    "12:30 PM",
    "1:00 PM",
    "1:30 PM",
    "2:00 PM",
    "2:30 PM",
    "3:00 PM",
    "3:30 PM",
    "4:00 PM",
    "4:30 PM",
    "5:00 PM",
    "5:30 PM",
  ];

  const getDates = () => {
    const dates = [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    // The strip normally runs today → +13 days. But when the operator picks a FAR date in the Calendar
    // View popup (e.g. Nov 19 while today is Oct 22), that day wasn't in the row at all → confusing
    // (Jose 2026-10-22). Re-anchor the 14-day window to START 3 days before the chosen date so its whole
    // week shows and the selected day is right there, highlighted + pickable.
    let start = new Date(today);
    if (selectedDate) {
      const sel = new Date(selectedDate); sel.setHours(0, 0, 0, 0);
      const daysOut = Math.round((sel.getTime() - today.getTime()) / 86400000);
      if (daysOut > 13) { start = new Date(sel); start.setDate(sel.getDate() - 3); }
    }
    for (let i = 0; i < 14; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // (2026-08-02) Availability now comes from the SHARED brain — shared/availability.ts —
  // so the POS, customer-portal and public-booking all read one source and can't drift.
  // These thin wrappers keep the render call sites (getSlotAvailability / isDayWorking /
  // isTimeSlotAvailable) unchanged; the logic lives in the shared module.
  // NO PAST TIMES FOR TODAY (Jose 2026-10-26): this Appointments-tab grid built its slots from a STATIC
  // 9–5:30 array tested only for schedule + conflict (isSlotBookable) — with NO now-filter. So at 3 PM it
  // still offered 10:30 / 12:00. Every OTHER picker (rebook, emergency, waitlist, portal, public,
  // receptionist) routes through bookableSlots() which drops `m < now` on today. This is the one surface
  // that bypassed the engine's grid builder, so the guard has to be re-applied here. DOCTRINE: no booking
  // surface offers a time earlier than NOW on the current local day.
  const isSlotPastToday = (date: Date | null, timeSlot: string): boolean => {
    if (!date) return false;
    const now = new Date();
    const sameDay = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
    if (!sameDay) return false;
    const slotMin = parseTimeToMinutes(timeSlot) ?? 0;
    return slotMin < (now.getHours() * 60 + now.getMinutes());
  };

  // EDIT-SHIFT AWARE (Jose 2026-10-28): fold any per-date shift override onto the barber so a changed
  // shift (extended/shortened hours, or an off/on flip) actually moves the bookable times — the engine
  // only reads weekly hours otherwise. UTC-safe: looks the shift up by the LOCAL toYMD(date).
  const withShift = <B extends Barber | null | undefined>(b: B, date: Date): B =>
    (b ? (applyShift(b as any, date, shiftService.getShift((b as any).id, toYMD(date)) as any) as any) : b);

  const isTimeSlotAvailable = (date: Date, timeSlot: string, barber: Barber | null): boolean => {
    if (!date || !barber) return false;
    if (isSlotPastToday(date, timeSlot)) return false; // can't book a time that already passed today
    const slotMinutes = parseTimeToMinutes(timeSlot) ?? 0;
    const appts = appointmentService.getAppointmentsByDate(format(date, 'yyyy-MM-dd'));
    return isSlotBookable(withShift(barber, date), date, slotMinutes, appts);
  };

  // ⚡ Who (if anyone) occupies a slot — for the manager squeeze grid's "busy · name" flag. (2026-10-21)
  const slotOccupant = (date: Date, timeSlot: string, barber: Barber | null): string | null => {
    if (!date || !barber) return null;
    const m = parseTimeToMinutes(timeSlot) ?? 0;
    const dur = selectedServices.reduce((s, sv) => s + resolveServiceDuration(sv.name, barber.id, { barberDurations: (barber as any)?.serviceDurations, fallback: sv.duration }), 0) || 30;
    const appts = appointmentService.getAppointmentsByDate(format(date, 'yyyy-MM-dd'));
    const occ = appts.find((a: any) => {
      if (a.barberId !== barber.id || a.status === 'cancelled') return false;
      const s = parseTimeToMinutes(a.time) ?? -1;
      if (s < 0) return false;
      const e = s + (a.duration || 30);
      return s < m + dur && m < e;
    });
    return occ ? (occ.clientName || 'Booked') : null;
  };

  // Off (not scheduled) vs Full (booked solid) — is anyone working this day?
  const isDayWorking = (date: Date, specificBarber?: Barber | null): boolean => {
    if (specificBarber) return getDaySchedule(withShift(specificBarber, date), date).working;
    return barbers.some(b => b.available !== false && getDaySchedule(withShift(b, date), date).working);
  };

  const getSlotAvailability = (date: Date, specificBarber?: Barber | null): number => {
    const appts = appointmentService.getAppointmentsByDate(format(date, 'yyyy-MM-dd'));
    if (specificBarber) return computeDayAvailability(withShift(specificBarber, date), date, appts).availableSlots;
    // No specific barber: sum available across every working (active) barber.
    return barbers
      .filter(b => b.available !== false && getDaySchedule(withShift(b, date), date).working)
      .reduce((sum, b) => sum + computeDayAvailability(withShift(b, date), date, appts).availableSlots, 0);
  };

  const toggleService = (service: Service) => {
    setSelectedServices((prev) => {
      const exists = prev.find((s) => s.id === service.id);
      if (exists) {
        return prev.filter((s) => s.id !== service.id);
      }
      return [...prev, service];
    });
  };

  const calculateTotal = () => {
    const subtotal = selectedServices.reduce(
      (sum, service) => sum + service.price,
      0,
    );
    const tax = subtotal * 0.085;
    const tip = subtotal * 0.2;
    return { subtotal, tax, tip, total: subtotal + tax + tip };
  };

  // Calculate totals for potential future use in checkout flow
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { subtotal, tax, tip, total } = calculateTotal();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-x-hidden pb-40 md:pb-24">
      {/* Full-Width Layout — uses all available space */}
      <div className="w-full px-4 py-4 md:px-6 md:py-5">

        {/* Top Tiles: Customer + Emergency — Equal width, symmetrical */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 mb-4">
          {/* Customer Search Tile */}
          <button
            onClick={() => setShowCustomerSearch(true)}
            className="rounded-xl px-4 py-4 text-white text-left transition-all shadow-lg min-h-[100px] flex flex-col justify-center hover:brightness-[1.08]"
            style={{
              // Matte gradient (Jose 2026-09-07): deeper, richer, non-glossy — reads premium next to
              // the warm theme instead of the flat bright blue/green.
              background: selectedCustomer
                ? 'linear-gradient(140deg, #2f7a57 0%, #16492f 100%)'
                : 'linear-gradient(140deg, #3a5382 0%, #1e2e49 100%)',
              boxShadow: '0 10px 26px -12px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)',
            }}
          >
            {selectedCustomer ? (
              <>
                <div className="flex items-start justify-between mb-1">
                  <span className="text-xs opacity-80 font-medium">Selected Customer</span>
                  <span
                    onClick={(e) => { e.stopPropagation(); setSelectedCustomer(null); }}
                    className="text-white/60 hover:text-white text-lg leading-none -mt-1"
                  >✕</span>
                </div>
                <div className="font-bold text-base md:text-lg truncate">{selectedCustomer.name}</div>
                <div className="text-sm opacity-90">{selectedCustomer.phone}</div>
                {selectedCustomer.visits >= 20 && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-white/20 text-white text-xs rounded-full font-semibold w-fit">VIP</span>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="font-semibold text-base">Customer Search</span>
                </div>
                <div className="text-sm opacity-80">Tap to select a customer</div>
                <div className="text-xs opacity-60 mt-1">{customers.length} customers • {customers.filter(c => c.visits >= 20).length} VIP</div>
              </>
            )}
          </button>

          {/* Emergency Booking Tile — Same grid cell */}
          <div className="min-h-[100px]">
            <EmergencyBookingTile
              appointments={existingAppointments}
              barbers={barbers}
              services={services}
              onBookingComplete={() => {
                setRefreshKey(prev => prev + 1);
                toast.success('🚨 Emergency booking created!');
              }}
              clientData={emergencySelectedClient}
            />
          </div>
        </div>

        {/* 1. SERVICES — Responsive card grid */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span>💈</span> Services
            </h3>
            {selectedServices.length > 0 && (
              <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 rounded-full">
                {selectedServices.length} selected • ${selectedServices.reduce((s, sv) => s + sv.price, 0)} • {selectedServices.reduce((s, sv) => s + sv.duration, 0)}min
              </span>
            )}
          </div>

          {/* Category Tabs */}
          <div className="flex gap-1 mb-3 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg overflow-x-auto">
            {(['all', 'hair', 'beard', 'special'] as const).map(cat => (
              <button
                key={cat}
                onClick={() => setServiceCategory(cat)}
                className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors whitespace-nowrap ${
                  serviceCategory === cat
                    ? "bg-white dark:bg-gray-600 shadow text-blue-600 dark:text-blue-400"
                    : "text-gray-500 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-600/50"
                }`}
              >
                {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                {cat !== 'all' && ` (${allServices[cat].length})`}
              </button>
            ))}
          </div>

          {services.length === 0 ? (
            <p className="text-center py-6 text-gray-400 text-sm">
              {serviceCategory === "all" ? "No services found. Add services in Service Management." : `No ${serviceCategory} services. Try "All".`}
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {services.map((service) => {
                const isSelected = selectedServices.some((s) => s.id === service.id);
                return (
                  <button
                    key={service.id}
                    onClick={() => toggleService(service)}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 ring-1 ring-blue-200 dark:ring-blue-800"
                        : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-blue-300 dark:hover:border-blue-500"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{service.name}</div>
                      <div className="text-xs text-gray-400">{service.duration} min</div>
                    </div>
                    <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                      <span className="text-sm font-bold text-gray-900 dark:text-white">${service.price}</span>
                      {isSelected && <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs">✓</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 2. BARBER — Responsive: scroll on mobile, grid on desktop */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-4">
          <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
            <span>✂️</span> Select Your Professional
            {selectedBarber && <span className="text-xs font-normal text-purple-600 dark:text-purple-400 ml-auto bg-purple-50 dark:bg-purple-900/30 px-2 py-0.5 rounded-full">{selectedBarber.name}</span>}
          </h3>
          {barbers.length === 0 ? (
            <p className="text-center py-4 text-gray-400 text-sm">No barbers available</p>
          ) : (
            <div className="flex md:grid md:grid-cols-5 lg:grid-cols-6 gap-3 overflow-x-auto md:overflow-visible pb-1 md:pb-0" style={{ WebkitOverflowScrolling: 'touch' }}>
              {barbers.map((barber, idx) => {
                const colors = ['from-purple-500 to-indigo-600','from-blue-500 to-cyan-600','from-green-500 to-emerald-600','from-orange-500 to-red-600','from-pink-500 to-rose-600','from-teal-500 to-cyan-600'];
                const initials = barber.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                const color = colors[idx % colors.length];
                const hasPhoto = barber.avatar?.startsWith('http');
                const isSelected = selectedBarber?.id === barber.id;

                return (
                  <button
                    key={barber.id}
                    onClick={() => barber.available && setSelectedBarber(barber)}
                    className={`flex-shrink-0 flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all min-w-[80px] ${
                      isSelected
                        ? "border-purple-500 bg-purple-50 dark:bg-purple-900/30 ring-2 ring-purple-200 dark:ring-purple-800"
                        : "border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500"
                    } ${!barber.available ? "opacity-50" : ""}`}
                  >
                    {hasPhoto ? (
                      <img src={barber.avatar} alt={barber.name} className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover border-3 border-white dark:border-gray-600 shadow-lg" />
                    ) : (
                      <div className={`w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br ${color} flex items-center justify-center shadow-lg`}>
                        <span className="text-base md:text-lg font-bold text-white">{initials}</span>
                      </div>
                    )}
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center leading-tight">{barber.name.split(' ')[0]}</span>
                    {isSelected && <span className="text-[10px] text-purple-600 dark:text-purple-400 font-bold">Selected</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 3. DATE — Horizontal scrollable pills */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
              <span>📅</span> Select Date
              {/* Chosen date — BIG + BOLD so staff always see the month/day they picked, not just the tiny
                  day pills (Jose 2026-10-22: Kelly couldn't tell which month she was on after the popup). */}
              {selectedDate && (
                <span className="px-3 py-1 rounded-lg bg-indigo-600 text-white text-base font-extrabold shadow-sm whitespace-nowrap">
                  {selectedDate.toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" })}
                </span>
              )}
            </h3>
            <button
              onClick={() => setShowCalendarPopup(!showCalendarPopup)}
              className="text-xs px-3 py-1 bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 flex-shrink-0"
            >
              Calendar View
            </button>
          </div>
          {/* ⚡ Manager squeeze — hoisted ABOVE the day pills so it UNLOCKS full days (same fix as reschedule):
              flip it, then full days + taken times become pickable to double-book a client in. */}
          {isManager && (
            <label className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 cursor-pointer select-none">
              <input type="checkbox" checked={squeezeMode} onChange={(e) => { setSqueezeMode(e.target.checked); setSelectedTime(''); }} className="accent-amber-500 w-4 h-4" />
              <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">⚡ Squeeze in — unlock full days &amp; double-book a taken slot (manager)</span>
            </label>
          )}
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
            {getDates().map((date) => {
              const isSelected = selectedDate?.toDateString() === date.toDateString();
              const isToday = new Date().toDateString() === date.toDateString();
              const working = isDayWorking(date, selectedBarber);      // scheduled to work?
              const slots = getSlotAvailability(date, selectedBarber);
              // Three states (2026-08-02): Off = not scheduled (grey), Full = booked solid (red), N slots = open.
              const dayLabel = !working ? "Off" : slots === 0 ? "Full" : `${slots} slots`;
              // ⚡ MANAGER SQUEEZE now also unlocks a barber's TIME-OFF / non-working days — not just
              // full-but-working days (Jose 2026-10-28: Kelly couldn't book over her own time off even with
              // the override on, because every day was gated on `working`). With squeeze on, any day is
              // bookable; the write passes allowOverlap so the schedule/double-book guard is bypassed.
              const squeezableDay = squeezeMode && (!working || slots === 0); // off OR full → manager can squeeze in
              const isBookable = squeezeMode || (working && slots > 0);

              return (
                <button
                  key={date.toISOString()}
                  onClick={() => isBookable && setSelectedDate(date)}
                  disabled={!isBookable}
                  className={`flex-shrink-0 px-3 py-2.5 rounded-lg border-2 min-w-[68px] text-center transition-colors ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-gray-900 dark:text-white"
                      : squeezableDay
                        ? "border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-gray-900 dark:text-white hover:border-amber-400"
                        : isToday && isBookable
                          ? "border-green-400 bg-green-50 dark:bg-green-900/30 text-gray-900 dark:text-white"
                          : !isBookable
                            ? "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 opacity-40 cursor-not-allowed"
                            : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-blue-400 text-gray-900 dark:text-white"
                  }`}
                >
                  <div className="text-xs text-gray-500 dark:text-gray-400">{date.toLocaleDateString("en-US", { weekday: "short" })}</div>
                  <div className="text-lg font-bold">{date.getDate()}</div>
                  <div className={`text-xs ${!working ? "text-gray-400 dark:text-gray-500" : squeezableDay ? "text-amber-600 dark:text-amber-400" : slots === 0 ? "text-red-500" : slots > 5 ? "text-green-500" : "text-yellow-500"}`}>
                    {squeezableDay ? (working ? "⚡ Full" : "⚡ Off") : dayLabel}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* 4. TIME — Responsive grid, only when date selected */}
        {selectedDate && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span>🕐</span> Available Times
                {selectedBarber && (
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                    {selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    {` • ${selectedBarber.name}`}
                  </span>
                )}
              </h3>
              {selectedTime && (
                <span className="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2.5 py-1 rounded-full">✓ {selectedTime}</span>
              )}
            </div>
            {selectedDate && timeSlots.every((t) => isSlotPastToday(selectedDate, t)) && (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No more openings today — pick another day above.</p>
            )}
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-2">
              {timeSlots.filter((time) => !isSlotPastToday(selectedDate, time)).map((time) => {
                const isAvailable = isTimeSlotAvailable(selectedDate, time, selectedBarber);
                if (squeezeMode) {
                  const who = !isAvailable ? slotOccupant(selectedDate, time, selectedBarber) : null;
                  const occupied = !!who;
                  return (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`py-2 rounded-lg border text-xs font-medium transition-all ${
                        selectedTime === time
                          ? "bg-blue-500 text-white border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800"
                          : occupied
                            ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:border-amber-400"
                            : "bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-700 dark:text-gray-200"
                      }`}
                    >
                      <div>{time}</div>
                      {occupied && <div className="text-[9px] opacity-80 leading-tight">busy{who ? ` · ${String(who).split(' ')[0]}` : ''}</div>}
                    </button>
                  );
                }
                return (
                  <button
                    key={time}
                    onClick={() => isAvailable && setSelectedTime(time)}
                    disabled={!isAvailable}
                    className={`py-2 rounded-lg border text-sm font-medium transition-all ${
                      selectedTime === time
                        ? "bg-blue-500 text-white border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800"
                        : isAvailable
                          ? "bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 text-gray-700 dark:text-gray-200"
                          : "bg-gray-50 dark:bg-gray-800 text-gray-300 dark:text-gray-600 border-gray-100 dark:border-gray-700 line-through cursor-not-allowed"
                    }`}
                  >
                    {time}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* STICKY BOTTOM BAR — Booking summary + Book button */}
      {(selectedServices.length > 0 || selectedBarber || selectedDate || selectedTime) && (
        <div className="fixed bottom-16 md:bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3 pb-safe z-[51] shadow-[0_-4px_12px_rgba(0,0,0,0.1)]">
          <div className="px-2 md:px-4">
            {/* Summary line */}
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 mb-2 flex-wrap">
              {selectedServices.length > 0 && (
                <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full font-medium">
                  ✂️ {selectedServices.map(s => s.name).join(', ')} • ${selectedServices.reduce((s, sv) => s + sv.price, 0)}
                </span>
              )}
              {selectedBarber && (
                <span className="bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded-full font-medium">
                  👤 {selectedBarber.name}
                </span>
              )}
              {selectedDate && (
                <span className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
                  📅 {selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </span>
              )}
              {selectedTime && (
                <span className="bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 px-2 py-0.5 rounded-full font-medium">
                  🕐 {selectedTime}
                </span>
              )}
            </div>
            {/* Book button */}
            <button
              onClick={() => {
                if (!selectedBarber) { toast.error("Select a professional"); return; }
                if (!selectedDate) { toast.error("Select a date"); return; }
                if (!selectedTime) { toast.error("Select a time"); return; }
                if (selectedServices.length === 0) { toast.error("Select a service"); return; }

                // Per-barber duration (Jose 2026-10-25): Diana's fade = 45 even if the menu default is 30.
                // Mirrors what the portal/public booking already do so a staff-booked appt STORES the right
                // length (the display/resolve layers trust the stored value, so it must be correct at write).
                const totalDuration = selectedServices.reduce((sum, s) =>
                  sum + resolveServiceDuration(s.name, selectedBarber.id, { barberDurations: (selectedBarber as any)?.serviceDurations, fallback: s.duration }), 0);
                const totalPrice = selectedServices.reduce((sum, s) => sum + s.price, 0);
                const clientName = selectedCustomer ? selectedCustomer.name : "Walk-in Client";
                const clientPhone = selectedCustomer ? selectedCustomer.phone : "(555) 000-0000";
                // NEW-CLIENT (2026-08-07): first-timer (0 prior visits) → 45-min min + "New Client" label.
                const mainNewClient = selectedCustomer
                  ? Number((selectedCustomer as any).visits ?? (selectedCustomer as any).totalVisits ?? 0) === 0
                  : isNewClient({ name: clientName, phone: clientPhone, email: (selectedCustomer as any)?.email }, clientDataService.getAllClients());

                const convertTo24Hour = (time12h: string) => {
                  const [time, modifier] = time12h.split(' ');
                  let [hours, minutes] = time.split(':');
                  if (!minutes) minutes = '00';
                  if (hours === '12') hours = modifier === 'AM' ? '00' : '12';
                  else if (modifier === 'PM') hours = String(parseInt(hours, 10) + 12);
                  return `${hours.padStart(2, '0')}:${minutes}`;
                };

                const appointmentData = {
                  clientId: selectedCustomer ? selectedCustomer.id : `temp-${Date.now()}`,
                  clientName, clientPhone,
                  barberId: selectedBarber.id,
                  barberName: selectedBarber.name,
                  service: selectedServices.map(s => s.name).join(" + "),
                  date: format(selectedDate, "yyyy-MM-dd"),
                  time: convertTo24Hour(selectedTime),
                  duration: totalDuration, // new-client decided in appointmentService.addAppointment
                  price: totalPrice,
                  status: "scheduled" as const,
                  source: "online" as const,
                  reminderSent: false,
                  notes: `Services: ${selectedServices.map(s => s.name).join(", ")}`,
                };

                const loadingToast = toast.loading("Booking...");
                const stopBookPerf = perfStart('book:appointments-tab'); // measure tap → booked (timing log)

                // ⚡ manager squeeze: allowOverlap bypasses the double-book guard; overrideAvailability bypasses
                // the barber-schedule/hours validator so an OFF day ("unlock full days") actually writes — without
                // it, a squeeze onto Kelly's day off still died with "Failed to book" (Jose 2026-10-29). local-first.
                appointmentService.addAppointment(appointmentData, { allowOverlap: squeezeMode, overrideAvailability: squeezeMode, background: true })
                  .then((newAppointment) => {
                    window.dispatchEvent(new CustomEvent("appointmentAdded"));
                    toast.dismiss(loadingToast);
                    stopBookPerf();
                    setConfirmedBookingDetails({
                      barber: selectedBarber, date: selectedDate,
                      time: selectedTime, services: [...selectedServices]
                    });
                    setBookingConfirmed(true);
                    setTimeout(() => {
                      setSelectedBarber(null); setSelectedDate(new Date());
                      setSelectedTime(''); setSelectedServices([]);
                    }, 100);
                  })
                  .catch((error: any) => {
                    toast.dismiss(loadingToast);
                    // NO-SHOW BLOCK: open the prepay + e-signature gate instead of a dead-end toast.
                    if (error?.code === 'NO_SHOW_BLOCK') {
                      setBlockGate({
                        notice: error.message,
                        apptData: appointmentData,
                        serviceLabel: appointmentData.service,
                        priceLabel: `$${appointmentData.price}`,
                        clientName: appointmentData.clientName,
                      });
                      return;
                    }
                    const msg = error instanceof Error && error.message.startsWith('Conflict:')
                      ? error.message.replace(/^Conflict:\s*/, '')
                      : 'Failed to book. Please try again.';
                    toast.error(msg);
                    console.error("Booking failed:", error);
                  });
              }}
              disabled={!selectedBarber || !selectedDate || !selectedTime || selectedServices.length === 0}
              className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                selectedBarber && selectedDate && selectedTime && selectedServices.length > 0
                  ? "bg-green-600 text-white hover:bg-green-700 active:scale-[0.98]"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {selectedBarber && selectedDate && selectedTime && selectedServices.length > 0
                ? `Book Appointment • $${selectedServices.reduce((s, sv) => s + sv.price, 0)}`
                : `Select ${!selectedServices.length ? 'services' : !selectedBarber ? 'a barber' : !selectedDate ? 'a date' : 'a time'} to book`
              }
            </button>
          </div>
        </div>
      )}

      {/* Enhanced Calendar Popup Modal */}
      {showCalendarPopup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Select Date</h3>
              <button
                onClick={() => setShowCalendarPopup(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4 px-2">
              <button
                onClick={() => {
                  const newMonth = new Date(calendarMonth);
                  newMonth.setMonth(newMonth.getMonth() - 1);
                  setCalendarMonth(newMonth);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                ←
              </button>
              <h4 className="font-semibold text-center">
                {calendarMonth.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </h4>
              <button
                onClick={() => {
                  const newMonth = new Date(calendarMonth);
                  newMonth.setMonth(newMonth.getMonth() + 1);
                  setCalendarMonth(newMonth);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                →
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 text-center text-sm">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="font-semibold py-2 text-gray-600">
                  {day}
                </div>
              ))}

              {(() => {
                const firstDay = new Date(
                  calendarMonth.getFullYear(),
                  calendarMonth.getMonth(),
                  1,
                );
                const lastDay = new Date(
                  calendarMonth.getFullYear(),
                  calendarMonth.getMonth() + 1,
                  0,
                );
                const startPadding = firstDay.getDay();
                const daysInMonth = lastDay.getDate();
                const totalCells =
                  Math.ceil((startPadding + daysInMonth) / 7) * 7;

                const cells = [];
                for (let i = 0; i < totalCells; i++) {
                  const dayNum = i - startPadding + 1;
                  const isCurrentMonth = dayNum > 0 && dayNum <= daysInMonth;
                  const cellDate = new Date(
                    calendarMonth.getFullYear(),
                    calendarMonth.getMonth(),
                    dayNum,
                  );
                  const isToday =
                    new Date().toDateString() === cellDate.toDateString();
                  const isSelected =
                    selectedDate?.toDateString() === cellDate.toDateString();
                  const isPast =
                    cellDate < new Date(new Date().setHours(0, 0, 0, 0));

                  cells.push(
                    <button
                      key={i}
                      onClick={() => {
                        if (isCurrentMonth && !isPast) {
                          setSelectedDate(cellDate);
                          setShowCalendarPopup(false);
                        }
                      }}
                      disabled={!isCurrentMonth || isPast}
                      className={`py-2 rounded-lg transition-all ${
                        !isCurrentMonth
                          ? "text-transparent cursor-default"
                          : isPast
                            ? "text-gray-300 cursor-not-allowed"
                            : isSelected
                              ? "bg-blue-500 text-white font-semibold"
                              : isToday
                                ? "bg-blue-100 text-blue-700 font-semibold"
                                : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {isCurrentMonth ? dayNum : ""}
                    </button>,
                  );
                }
                return cells;
              })()}
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  setSelectedDate(new Date());
                  setShowCalendarPopup(false);
                }}
                className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  setSelectedDate(tomorrow);
                  setShowCalendarPopup(false);
                }}
                className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
              >
                Tomorrow
              </button>
              <button
                onClick={() => setShowCalendarPopup(false)}
                className="flex-1 py-2 border border-gray-300 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Customer Search - Slide from Right */}
      {showCustomerSearch && (
        <div className="fixed inset-0 z-[9998]">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60"
            style={{ backdropFilter: 'blur(4px)' }}
            onClick={() => setShowCustomerSearch(false)}
          />
          {/* Panel */}
          <div
            className="absolute top-0 right-0 h-[100dvh] w-full max-w-md bg-white shadow-2xl flex flex-col"
            style={{ animation: 'slideInRight 0.3s ease-out' }}
          >
            {/* Header */}
            <div className="flex-shrink-0 bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Customer Search</h2>
              <button
                onClick={() => setShowCustomerSearch(false)}
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Search + Filters */}
            <div className="flex-shrink-0 p-4 space-y-3 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, phone, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2.5 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-base md:text-sm"
                  autoFocus
                />
                <svg className="absolute left-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setFilterType("all")}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    filterType === "all" ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  All ({customers.length})
                </button>
                <button
                  onClick={() => setFilterType("vip")}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    filterType === "vip" ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  VIP ({customers.filter((c) => c.visits >= 20).length})
                </button>
                <button
                  onClick={() => setFilterType("new")}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    filterType === "new" ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  New ({customers.filter((c) => c.visits < 5).length})
                </button>
                <button
                  onClick={() => setFilterType("due")}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    filterType === "due" ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Due ({customers.filter((c) => c.lastVisit.includes("month") || c.lastVisit.includes("weeks")).length})
                </button>
              </div>

              <div className="text-xs text-gray-500">
                Showing {filteredCustomers.length} of {customers.length} customers
              </div>
            </div>

            {/* Customer List - Scrollable */}
            <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="p-4 space-y-2">
                {filteredCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-all active:scale-[0.98]"
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setShowCustomerSearch(false);
                      toast.success(`Selected ${customer.name} for booking`);
                    }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm">{customer.name}</h3>
                        {customer.visits >= 20 && (
                          <span className="px-1.5 py-0.5 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-[10px] rounded-full font-semibold">VIP</span>
                        )}
                        {customer.visits < 5 && (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-800 text-[10px] rounded-full">NEW</span>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickBook(customer);
                        }}
                        className="px-3 py-1 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        Quick Book
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 space-y-0.5">
                      <p>{customer.phone} {customer.email && `• ${customer.email}`}</p>
                      <p>{customer.visits} visits • Last: {customer.lastVisit} • Spent: ${customer.totalSpent}</p>
                    </div>
                    {customer.nextAppointment && (
                      <div className="mt-1 px-2 py-1 bg-blue-50 rounded text-xs text-blue-700 font-medium">
                        Next: {customer.nextAppointment}
                      </div>
                    )}
                  </div>
                ))}
                {filteredCustomers.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p>No customers found</p>
                    <p className="text-sm mt-1">Try adjusting your search or filters</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-gray-200 p-4 pb-safe flex gap-3">
              <button
                onClick={() => {
                  setShowNewCustomerModal(true);
                }}
                className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Add New Customer
              </button>
              <button
                onClick={() => setShowCustomerSearch(false)}
                className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Modal */}
      {showCustomerDetails && selectedCustomerDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[600px] max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Customer Details</h2>
              <button
                onClick={() => {
                  setShowCustomerDetails(false);
                  setSelectedCustomerDetails(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Customer Header */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg mb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-bold">
                        {selectedCustomerDetails.name}
                      </h3>
                      {selectedCustomerDetails.visits >= 20 && (
                        <span className="px-2 py-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-xs rounded-full font-semibold">
                          VIP CUSTOMER
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600">
                      Member since {selectedCustomerDetails.memberSince}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-purple-600">
                      {selectedCustomerDetails.visits}
                    </p>
                    <p className="text-sm text-gray-600">Total Visits</p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Contact Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <span>{selectedCustomerDetails.phone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <span>{selectedCustomerDetails.email}</span>
                  </div>
                </div>
              </div>

              {/* Visit Statistics */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Last Visit</p>
                  <p className="font-semibold">
                    {selectedCustomerDetails.lastVisit}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Favorite Service</p>
                  <p className="font-semibold">
                    {selectedCustomerDetails.favorite}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Total Spent</p>
                  <p className="font-semibold text-green-600">
                    ${selectedCustomerDetails.totalSpent}
                  </p>
                </div>
              </div>

              {/* Next Appointment */}
              {selectedCustomerDetails.nextAppointment && (
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-4">
                  <p className="text-sm text-blue-600 font-semibold mb-1">
                    Next Appointment
                  </p>
                  <p className="text-blue-800">
                    {selectedCustomerDetails.nextAppointment}
                  </p>
                </div>
              )}

              {/* Customer Notes */}
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Notes</h4>
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                  <p className="text-sm">{selectedCustomerDetails.notes}</p>
                </div>
              </div>

              {/* Tags */}
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedCustomerDetails.tags.map(
                    (tag: string, index: number) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ),
                  )}
                </div>
              </div>

              {/* Recent History */}
              <div>
                <h4 className="font-semibold mb-2">Recent History</h4>
                <div className="space-y-2">
                  <div className="border-l-2 border-purple-500 pl-3 py-1">
                    <p className="text-sm font-medium">
                      {selectedCustomerDetails.favorite}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedCustomerDetails.lastVisit}
                    </p>
                  </div>
                  <div className="border-l-2 border-gray-300 pl-3 py-1">
                    <p className="text-sm font-medium">Hair Color</p>
                    <p className="text-xs text-gray-500">3 weeks ago</p>
                  </div>
                  <div className="border-l-2 border-gray-300 pl-3 py-1">
                    <p className="text-sm font-medium">Haircut & Style</p>
                    <p className="text-xs text-gray-500">6 weeks ago</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t flex gap-3">
              <button
                onClick={() => {
                  console.log('Book Appointment button clicked!');
                  console.log('selectedCustomerDetails:', selectedCustomerDetails);
                  console.log('selectedBarber:', selectedBarber);
                  console.log('selectedDate:', selectedDate);
                  console.log('selectedTime:', selectedTime);
                  console.log('selectedServices:', selectedServices);
                  handleQuickBook(selectedCustomerDetails);
                  setShowCustomerDetails(false);
                }}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Book Appointment
              </button>
              <button
                onClick={() => {
                  toast.success("Opening message composer");
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Send Message
              </button>
              <button
                onClick={() => {
                  setShowCustomerDetails(false);
                  setSelectedCustomerDetails(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confetti "success moment" when a booking confirms (random animation each time). */}
      <BookingConfettiTrigger active={bookingConfirmed} />

      {/* Booking Confirmation Modal */}
      {bookingConfirmed && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fade-in">
            <div className="text-center mb-4">
              <div className="mx-auto flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                Booking Confirmed!
              </h3>
              <p className="text-gray-600 mt-2">
                Your appointment has been successfully booked
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Professional:</span>
                <span className="font-semibold">{confirmedBookingDetails?.barber?.name || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date:</span>
                <span className="font-semibold">
                  {confirmedBookingDetails?.date?.toLocaleDateString() || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Time:</span>
                <span className="font-semibold">{confirmedBookingDetails?.time || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Services:</span>
                <span className="font-semibold">
                  {confirmedBookingDetails?.services?.map((s) => s.name).join(", ") || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total:</span>
                <span className="font-bold text-lg">
                  ${confirmedBookingDetails?.services?.reduce((sum, s) => sum + s.price, 0) || 0}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setBookingConfirmed(false);
                  setConfirmedBookingDetails(null);
                  // Reset form
                  setSelectedBarber(null);
                  setSelectedDate(null);
                  setSelectedTime(null);
                  setSelectedServices([]);
                  setSelectedCustomer(null);
                  // Scroll to top
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 font-semibold transition-all"
              >
                Book Another Appointment
              </button>
              <button
                onClick={() => {
                  setBookingConfirmed(false);
                  setConfirmedBookingDetails(null);
                  // Navigate to calendar tab with the appointment date
                  const appointmentDate = confirmedBookingDetails?.date
                    ? ymd(confirmedBookingDetails.date)
                    : undefined;
                  navigate("/operations", {
                    state: {
                      openCalendar: true,
                      appointmentDate: appointmentDate
                    }
                  });
                }}
                className="w-full py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-all"
              >
                View Calendar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Customer - Slide from Right (2nd level, higher z-index) */}
      {showNewCustomerModal && (
        <div className="fixed inset-0 z-[10001]">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60"
            style={{ backdropFilter: 'blur(4px)' }}
            onClick={() => setShowNewCustomerModal(false)}
          />
          {/* Panel */}
          <div
            className="absolute top-0 right-0 h-[100dvh] w-full max-w-md bg-white shadow-2xl flex flex-col"
            style={{ animation: 'slideInRight 0.3s ease-out' }}
          >
            {/* Header */}
            <div className="flex-shrink-0 bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Add New Customer</h2>
              <button
                onClick={() => setShowNewCustomerModal(false)}
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Form - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={newCustomerForm.firstName}
                      onChange={(e) => setNewCustomerForm({ ...newCustomerForm, firstName: e.target.value })}
                      className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      placeholder="John"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={newCustomerForm.lastName}
                      onChange={(e) => setNewCustomerForm({ ...newCustomerForm, lastName: e.target.value })}
                      className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    value={newCustomerForm.phone}
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, phone: e.target.value })}
                    className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={newCustomerForm.email}
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, email: e.target.value })}
                    className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    placeholder="john.doe@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Birthday</label>
                  <input
                    type="date"
                    value={newCustomerForm.birthday}
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, birthday: e.target.value })}
                    className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={newCustomerForm.notes}
                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, notes: e.target.value })}
                    className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    rows={3}
                    placeholder="Any special preferences or notes..."
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-gray-200 p-4 pb-safe flex gap-3">
              <button
                onClick={async () => {
                  if (!newCustomerForm.firstName || !newCustomerForm.lastName || !newCustomerForm.phone) {
                    toast.error('Please fill in all required fields');
                    return;
                  }

                  try {
                    const newClient = await clientDataService.addClient({
                      firstName: newCustomerForm.firstName,
                      lastName: newCustomerForm.lastName,
                      email: newCustomerForm.email || '',
                      phone: newCustomerForm.phone,
                      preferredBarber: '',
                      membershipType: 'regular' as const,
                      joinDate: new Date(),
                      totalVisits: 0,
                      totalSpent: 0,
                      averageTicket: 0,
                      rebookingRate: 0,
                      noShowCount: 0,
                      cancellationCount: 0,
                      notes: newCustomerForm.notes || '',
                      tags: [],
                      status: 'active' as const,
                      loyaltyPoints: 0,
                      birthday: newCustomerForm.birthday || undefined,
                      preferences: {
                        appointmentReminders: true,
                        marketingEmails: false,
                        smsNotifications: true
                      }
                    });

                    const newCustomer = {
                      id: newClient.id,
                      name: `${newCustomerForm.firstName} ${newCustomerForm.lastName}`,
                      phone: newCustomerForm.phone,
                      email: newCustomerForm.email,
                      lastVisit: 'Never',
                      visits: 0,
                      favorite: 'Haircut',
                      totalSpent: 0,
                      notes: newCustomerForm.notes,
                      nextAppointment: null,
                      memberSince: new Date().getFullYear().toString(),
                      tags: []
                    };

                    setSelectedCustomer(newCustomer);
                    setCustomers(prev => [...prev, newCustomer]);
                    setNewCustomerForm({ firstName: '', lastName: '', email: '', phone: '', birthday: '', notes: '' });
                    setShowNewCustomerModal(false);
                    setShowCustomerSearch(false);
                    toast.success(`Customer ${newCustomer.name} added successfully!`);
                  } catch (error) {
                    console.error('Error adding customer:', error);
                    toast.error('Failed to add customer. Please try again.');
                  }
                }}
                className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                Add Customer
              </button>
              <button
                onClick={() => {
                  setShowNewCustomerModal(false);
                  setNewCustomerForm({ firstName: '', lastName: '', email: '', phone: '', birthday: '', notes: '' });
                }}
                className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NO-SHOW BLOCK: in-person prepay + e-signature gate. On agree, re-books with the agreement. */}
      <NoShowAgreementModal
        open={!!blockGate}
        notice={blockGate?.notice || ''}
        clientName={blockGate?.clientName}
        serviceLabel={blockGate?.serviceLabel}
        priceLabel={blockGate?.priceLabel}
        submitting={agreeSubmitting}
        onClose={() => setBlockGate(null)}
        onAgree={submitBlockedPrepaid}
      />
    </div>
  );
};

export default CompleteBookingSystem;
